/**
 * Cliente do gateway — a máquina de estados que mantém a ligação viva.
 *
 * Hello → Identify (ou Resume) → Ready → stream de eventos, com heartbeat nos
 * dois sentidos. Três coisas que parecem detalhe e são o produto inteiro:
 *
 *  1. RESUME. Uma queda de Wi-Fi de 3 segundos não pode custar um Ready inteiro
 *     (dezenas de KB e um flash de UI vazia). Guardamos `sessionId` + `seq` e
 *     pedimos só o atraso — dentro da janela em que o servidor ainda tem buffer.
 *  2. ACK DO HEARTBEAT. Um socket zombie fica "aberto" durante minutos: o
 *     `readyState` diz OPEN e não chega nada. O que detecta isso é a ausência
 *     de ack, não o evento `close` — que nunca vem.
 *  3. RECONEXÃO COM JITTER. Se a API reiniciar, todos os clientes voltam ao
 *     mesmo tempo e derrubam-na outra vez. O jitter espalha-os.
 *
 * O token não aparece aqui: o cookie `gv_token` é httpOnly e o browser envia-o
 * no handshake. O `token: ""` no Identify é deliberado — o servidor cai no
 * cookie (ver `gateway.service.ts`).
 */

import {
  ClientOp,
  type ClientPayload,
  type Facing,
  GATEWAY_VERSION,
  type GatewayEvent,
  HEARTBEAT_TIMEOUT_MS,
  NON_RESUMABLE_CLOSE_CODES,
  type ReadyPayload,
  SESSION_RESUME_WINDOW_MS,
  type SelectableStatus,
  ServerOp,
  type ServerPayload,
  type Snowflake,
  TYPING_TTL_MS,
} from "@greenvoice/shared";

import { resolveWs } from "./host";

export type GatewayStatus = "idle" | "connecting" | "resuming" | "ready" | "offline" | "dead";

export interface GatewayHandlers {
  onStatus(status: GatewayStatus, detail?: string): void;
  onReady(payload: ReadyPayload): void;
  onEvent(event: GatewayEvent): void;
  /**
   * Um PEDIDO foi recusado (opcode desconhecido, rate-limit, sem permissão) com
   * a ligação de pé. Opcional de propósito: quem não quiser saber ignora, mas
   * quem ignorar em silêncio fica sem perceber porque é que a acção não pegou —
   * foi assim que uma API velha, que não conhecia os opcodes do escritório, deu
   * um botão de trancar que não fazia rigorosamente nada e não dizia porquê.
   */
  onError?(code: number, message: string): void;
}

// Mesmo princípio que a API: resolve o host da página ANTES de trocar http→ws,
// para o gateway acompanhar quem abre a app num IP de LAN, não só o localhost.
// Um `PUBLIC_WS_URL` que seja CAMINHO (`/gateway`, o que a ponte usa) monta-se
// sobre a origem da página — e aí não há http→ws a fazer, porque não há esquema.
const WS_URL = resolveWs(
  import.meta.env.PUBLIC_WS_URL ?? derivarDaApi(import.meta.env.PUBLIC_API_URL ?? "http://localhost:4000"),
);

/** Sem `PUBLIC_WS_URL`, o gateway vive no `/gateway` da própria API. */
function derivarDaApi(api: string): string {
  if (api.startsWith("/") || api === "") return "/gateway";
  return `${api.replace(/\/+$/, "").replace(/^http/, "ws")}/gateway`;
}

/** Nunca menos de dois: um único pedido sem resposta pode ser um pacote
 *  perdido, e derrubar a ligação por causa dele é trocar um corte por outro. */
const BATIDAS_MINIMAS = 2;

/** O prazo do protocolo traduzido para PEDIDOS, à cadência que o servidor
 *  mandou no Hello. É a mesma tolerância de sempre (~40s a 20s de intervalo),
 *  contada de uma maneira que um temporizador atrasado não falsifica. */
function batidasAte(prazoMs: number, intervaloMs: number): number {
  return Math.max(BATIDAS_MINIMAS, Math.floor(prazoMs / Math.max(1, intervaloMs)));
}

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

export class GatewayClient {
  private socket: WebSocket | undefined;
  private status: GatewayStatus = "idle";

  /** Estado de sessão — o que torna o Resume possível. */
  private sessionId: string | undefined;
  private seq = 0;
  private disconnectedAt = 0;

  private heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private lastAckAt = 0;
  /**
   * Heartbeats enviados a que ainda ninguém respondeu.
   *
   * Conta-se PEDIDOS SEM RESPOSTA e não tempo de parede, e é essa a diferença
   * entre uma ligação morta e um temporizador que não correu. Um separador
   * escondido há mais de cinco minutos tem os `setInterval` reduzidos a um por
   * minuto; um portátil fechado não corre nenhum. Em ambos os casos o relógio
   * avança e o `lastAckAt` fica para trás — sem que a ligação tenha nada de
   * errado.
   */
  private semResposta = 0;
  /** Quando saiu o último heartbeat. Zero = ainda nenhum nesta ligação. */
  private lastBeatAt = 0;
  /** Quantos pedidos sem resposta dão a ligação por morta — vem do prazo do
   *  protocolo e da cadência que o servidor pediu. */
  private limiteSemResposta = BATIDAS_MINIMAS;
  private attempt = 0;
  /** Desligado à mão: não voltar a ligar sozinho. */
  private stopped = false;
  private readonly typingSentAt = new Map<Snowflake, number>();

  constructor(private readonly handlers: GatewayHandlers) {}

  // ── ciclo de vida ───────────────────────────────────────────

  start(): void {
    this.stopped = false;
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.onOnline);
      document.addEventListener("visibilitychange", this.onVisibility);
    }
    this.open();
  }

  stop(): void {
    this.stopped = true;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.onOnline);
      document.removeEventListener("visibilitychange", this.onVisibility);
    }
    this.clearTimers();
    this.sessionId = undefined;
    this.seq = 0;
    this.socket?.close(1000, "cliente a sair");
    this.socket = undefined;
    this.setStatus("idle");
  }

  private open(): void {
    if (this.stopped) return;
    this.clearTimers();

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      this.setStatus("offline", "sem rede");
      return;
    }

    this.setStatus(this.canResume() ? "resuming" : "connecting");

    const socket = new WebSocket(WS_URL);
    this.socket = socket;

    socket.onmessage = (raw) => this.onMessage(raw);
    socket.onerror = () => {
      // O evento de erro do WebSocket não traz nada de útil por desenho
      // (privacidade). O `close` que vem a seguir é que decide o que fazer.
    };
    socket.onclose = (event) => this.onClose(event);
  }

  private onClose(event: CloseEvent): void {
    this.clearTimers();
    this.socket = undefined;
    this.disconnectedAt = Date.now();

    if (this.stopped) return;

    if (NON_RESUMABLE_CLOSE_CODES.has(event.code)) {
      // Credenciais ou versão erradas: insistir só gasta bateria.
      this.sessionId = undefined;
      this.setStatus("dead", event.reason || "sessão recusada");
      return;
    }

    this.scheduleReconnect(event.reason);
  }

  private scheduleReconnect(reason?: string): void {
    this.attempt += 1;
    const backoff = Math.min(BACKOFF_BASE_MS * 2 ** (this.attempt - 1), BACKOFF_MAX_MS);
    // Jitter completo: sem ele, N clientes reconectam em uníssono e voltam a
    // derrubar a API que acabou de subir.
    const delay = Math.round(backoff * (0.5 + Math.random() * 0.5));
    this.setStatus("connecting", reason ? `${reason} — nova tentativa em ${Math.round(delay / 1000)}s` : undefined);
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private onOnline = (): void => {
    if (this.stopped) return;
    this.attempt = 0;
    this.open();
  };

  private onVisibility = (): void => {
    if (this.stopped || document.visibilityState !== "visible") return;
    if (this.status === "ready" || this.socket) return;
    // Voltar ao separador é o momento em que o utilizador REPARA que está
    // desligado. Não o deixar à espera do backoff.
    this.attempt = 0;
    this.open();
  };

  // ── protocolo ───────────────────────────────────────────────

  private onMessage(raw: MessageEvent<unknown>): void {
    if (typeof raw.data !== "string") return;

    let payload: ServerPayload;
    try {
      payload = JSON.parse(raw.data) as ServerPayload;
    } catch {
      return;
    }

    switch (payload.op) {
      case ServerOp.Hello:
        this.onHello(payload.heartbeatIntervalMs);
        break;

      case ServerOp.Ready:
        this.seq = payload.seq;
        this.sessionId = payload.data.sessionId;
        this.attempt = 0;
        this.lastAckAt = Date.now();
        this.setStatus("ready");
        this.handlers.onReady(payload.data);
        break;

      case ServerOp.Resumed:
        // A sessão do socket anterior continua viva do lado de lá (com os espaços
        // em que estávamos), e o atraso já veio. Um resume sem nada em atraso não
        // trazia mensagem nenhuma: ficava-se "a retomar" para sempre, de ligação
        // boa — e tudo o que depende de estarmos ligados dava-se por perdido.
        this.seq = payload.seq;
        this.attempt = 0;
        this.lastAckAt = Date.now();
        this.setStatus("ready");
        break;

      case ServerOp.HeartbeatAck:
        this.lastAckAt = Date.now();
        break;

      case ServerOp.Event:
        this.seq = payload.seq;
        // Um Resume entrega os eventos em atraso ANTES de qualquer coisa nova;
        // chegar aqui já significa que a ligação está de pé.
        if (this.status !== "ready") {
          this.attempt = 0;
          this.lastAckAt = Date.now();
          this.setStatus("ready");
        }
        this.handlers.onEvent(payload.data);
        break;

      case ServerOp.Invalidate:
        if (!payload.resumable) {
          this.sessionId = undefined;
          this.seq = 0;
        }
        this.setStatus("connecting", payload.reason);
        this.socket?.close(1000, "invalidada");
        break;

      case ServerOp.Error:
        // Um Error é a recusa de UM PEDIDO, não a queda da ligação: o socket que
        // o trouxe está, por definição, vivo. Dizer "connecting" aqui era mentir
        // sobre o estado global por causa de um opcode que o servidor não conhece
        // ou de um rate-limit — e tudo o que depende de estarmos ligados (a tranca
        // da porta) desligava-se sozinho com a ligação intacta. Só credenciais ou
        // versão erradas matam mesmo a sessão; o resto reporta-se e segue.
        if (NON_RESUMABLE_CLOSE_CODES.has(payload.code)) {
          this.setStatus("dead", payload.message);
          break;
        }
        // Nada de mexer no status: manter-se o que está. Se já estávamos ligados,
        // continuamos ligados; se ainda vamos a meio do handshake, continuamos a
        // meio dele. Um pedido recusado não é uma ligação perdida.
        this.lastAckAt = Date.now();
        this.handlers.onError?.(payload.code, payload.message);
        break;
    }
  }

  private onHello(heartbeatIntervalMs: number): void {
    if (this.canResume() && this.sessionId) {
      this.send({ op: ClientOp.Resume, token: "", sessionId: this.sessionId, seq: this.seq });
    } else {
      this.sessionId = undefined;
      this.seq = 0;
      this.send({ op: ClientOp.Identify, token: "", version: GATEWAY_VERSION });
    }

    this.lastAckAt = Date.now();
    this.lastBeatAt = 0;
    this.semResposta = 0;
    this.limiteSemResposta = batidasAte(HEARTBEAT_TIMEOUT_MS, heartbeatIntervalMs);
    this.heartbeatTimer = setInterval(() => this.beat(), heartbeatIntervalMs);
  }

  /**
   * O sinal de vida — e o julgamento de quando a ligação está morta.
   *
   * A regra era `agora - lastAckAt > TIMEOUT`, e derrubava a ligação de quem
   * não tinha problema nenhum: bastava o separador ficar para trás (ou a tampa
   * do portátil fechar-se) para o temporizador parar, o relógio continuar, e
   * o primeiro `beat` ao acordar concluir que a ligação tinha morrido. O
   * socket fechava, o servidor via a última sessão cair — e A CHAMADA CAÍA COM
   * ELA. Era o *«fica ausente muito tempo e sai da call»* visto deste lado.
   *
   * Contar pedidos sem resposta separa as duas coisas: se o temporizador não
   * correu, também não houve pedidos por responder, e não há nada a concluir.
   * Dois seguidos sem ack já são ~40s de silêncio COM pedidos enviados, que é
   * o que o `HEARTBEAT_TIMEOUT_MS` sempre quis dizer.
   */
  private beat(): void {
    if (this.lastAckAt >= this.lastBeatAt) this.semResposta = 0;
    else this.semResposta += 1;

    if (this.semResposta >= this.limiteSemResposta) {
      // Ligação zombie: `close()` educado pode nunca completar, portanto o
      // caminho honesto é fechar já e deixar o onclose reconectar.
      this.socket?.close(4000, "heartbeat sem resposta");
      return;
    }

    this.lastBeatAt = Date.now();
    this.send({ op: ClientOp.Heartbeat, seq: this.seq });
  }

  private canResume(): boolean {
    return this.sessionId !== undefined && this.seq > 0 && Date.now() - this.disconnectedAt < SESSION_RESUME_WINDOW_MS;
  }

  // ── envio ───────────────────────────────────────────────────

  private send(payload: ClientPayload): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  // `SelectableStatus` e não `PresenceStatus`: OFFLINE não é uma escolha, é o
  // que se é sem sessão. Com o tipo largo, um `updatePresence("OFFLINE")` num
  // sítio qualquer compilava e o servidor deitava-o fora sem dizer nada.
  updatePresence(status: SelectableStatus, statusText?: string | null): void {
    this.send(
      statusText === undefined
        ? { op: ClientOp.UpdatePresence, status }
        : { op: ClientOp.UpdatePresence, status, statusText },
    );
  }

  /**
   * O servidor deixa o typing expirar sozinho ao fim de `TYPING_TTL_MS`, por
   * isso reenviar a cada tecla é desperdício puro: um refresh a meio da janela
   * chega para o indicador nunca piscar.
   */
  beginTyping(channelId: Snowflake): void {
    const last = this.typingSentAt.get(channelId) ?? 0;
    if (Date.now() - last < TYPING_TTL_MS / 2) return;
    if (this.send({ op: ClientOp.BeginTyping, channelId })) {
      this.typingSentAt.set(channelId, Date.now());
    }
  }

  endTyping(channelId: Snowflake): void {
    this.typingSentAt.delete(channelId);
    this.send({ op: ClientOp.EndTyping, channelId });
  }

  requestMembers(serverId: Snowflake, after?: Snowflake, limit?: number): void {
    this.send({
      op: ClientOp.RequestMembers,
      serverId,
      ...(after === undefined ? {} : { after }),
      ...(limit === undefined ? {} : { limit }),
    });
  }

  updateVoiceState(state: {
    channelId: Snowflake | null;
    selfMute?: boolean;
    selfDeaf?: boolean;
    screenSharing?: boolean;
    cameraOn?: boolean;
  }): void {
    this.send({ op: ClientOp.UpdateVoiceState, ...state });
  }

  // ── escritório 2D ───────────────────────────────────────────
  // Entrar pede o snapshot (o servidor responde com SpaceState); a partir daí
  // só se envia a própria posição. É fire-and-forget de propósito: uma posição
  // perdida por um socket a fechar não vale um erro — a seguinte corrige-a.

  enterSpace(spaceId: Snowflake): void {
    this.send({ op: ClientOp.EnterSpace, spaceId });
  }

  updatePosition(spaceId: Snowflake, x: number, y: number, facing: Facing): void {
    this.send({ op: ClientOp.UpdatePosition, spaceId, x, y, facing });
  }

  leaveSpace(spaceId: Snowflake): void {
    this.send({ op: ClientOp.LeaveSpace, spaceId });
  }

  /** Mexe no ferrolho da sala onde se está. O servidor valida que se está mesmo
   *  lá dentro e responde com `RoomLockUpdate` — nunca se assume localmente. */
  lockRoom(spaceId: Snowflake, roomId: string, locked: boolean): void {
    this.send({ op: ClientOp.LockRoom, spaceId, roomId, locked });
  }

  /** Bate à porta de uma sala trancada. Só quem lá está dentro é notificado. */
  knock(spaceId: Snowflake, roomId: string): void {
    this.send({ op: ClientOp.Knock, spaceId, roomId });
  }

  /** Responde a quem bateu. Aceitar destranca a porta; recusar só avisa. */
  answerKnock(spaceId: Snowflake, roomId: string, userId: Snowflake, accept: boolean): void {
    this.send({ op: ClientOp.AnswerKnock, spaceId, roomId, userId, accept });
  }

  /** Balão de texto por cima do avatar — o chat de quem não tem microfone. */
  say(spaceId: Snowflake, text: string): void {
    this.send({ op: ClientOp.Say, spaceId, text });
  }

  /** Chama a atenção de uma pessoa: barulho no ecrã dela, abanão no de todos. */
  poke(spaceId: Snowflake, targetId: Snowflake): void {
    this.send({ op: ClientOp.Poke, spaceId, targetId });
  }

  /** Atira uma bola de neve para um ponto do mundo, apontada (ou não) a alguém. */
  bolaDeNeve(spaceId: Snowflake, x: number, y: number, targetId: Snowflake | null): void {
    this.send({ op: ClientOp.BolaDeNeve, spaceId, x: Math.round(x), y: Math.round(y), targetId });
  }

  /** Liga a uma pessoa do escritório: toca do lado dela, esteja onde estiver no mapa. */
  ligar(spaceId: Snowflake, targetId: Snowflake): void {
    this.send({ op: ClientOp.Ligar, spaceId, targetId });
  }

  /** Atende (`true`) ou recusa uma ligação que está a tocar. */
  atenderLigacao(ligacaoId: string, aceitar: boolean): void {
    this.send({ op: ClientOp.AtenderLigacao, ligacaoId, aceitar });
  }

  /** Cancela (a tocar) ou desliga (em curso). */
  desligarLigacao(ligacaoId: string): void {
    this.send({ op: ClientOp.DesligarLigacao, ligacaoId });
  }

  // ── interno ─────────────────────────────────────────────────

  private setStatus(status: GatewayStatus, detail?: string): void {
    this.status = status;
    this.handlers.onStatus(status, detail);
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.heartbeatTimer = undefined;
    this.reconnectTimer = undefined;
  }
}
